/**
 * ---------------------------------------------------------------------------------------------------------------------
 * SWIPER LOOP FIX
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * The theme loads Swiper 11. Two things changed between Swiper 8 and 11 that silently break sliders written for the
 * older API:
 *
 *   1. `loopedSlides` was removed (it is `loopAdditionalSlides` now), so passing it does nothing.
 *   2. Loop mode needs at least `slidesPerView * 2` slides. With fewer, Swiper logs a loop warning and the track ends
 *      up short — slides sit off centre and the space where the next slide should be renders empty.
 *
 * This file patches the global Swiper constructor once, so every slider in the theme is covered without touching each
 * section. For any slider that asks for `loop`, it:
 *
 *   - translates the old `loopedSlides` option,
 *   - clones whole sets of slides into the wrapper until there are enough for the widest breakpoint, so the loop is
 *     seamless instead of disabled,
 *   - keeps the pagination showing one bullet per real slide, with the right one active while a clone is on screen,
 *   - turns on the observers and re-measures once the images inside have loaded, which is what leaves a slide blank
 *     when it is measured before its image has a size.
 *
 * Clones are marked with `data-swiper-loop-fill`. A `swiper-loop-fix:slides-cloned` event fires on the container so a
 * section can bind behaviour to them (the before/after handles do this).
 */
(function () {
  'use strict';

  var MAX_SLIDES = 60; // runaway guard when a slider has very few slides and a very wide slidesPerView
  var FALLBACK_SLIDES_PER_VIEW = 3; // used when slidesPerView is "auto" and cannot be measured

  function toNumber(value) {
    if (value === 'auto' || value === undefined || value === null) return null;
    var parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  }

  function usesAutoWidth(params) {
    if (params.slidesPerView === 'auto') return true;
    if (!params.breakpoints) return false;
    return Object.keys(params.breakpoints).some(function (key) {
      return params.breakpoints[key] && params.breakpoints[key].slidesPerView === 'auto';
    });
  }

  /* The widest slidesPerView across the base params and every breakpoint — the loop has to satisfy the largest one. */
  function maxSlidesPerView(element, params, slides) {
    var values = [];
    var base = toNumber(params.slidesPerView);

    if (base !== null) values.push(base);

    if (params.breakpoints) {
      Object.keys(params.breakpoints).forEach(function (key) {
        var breakpoint = params.breakpoints[key];
        var value = breakpoint ? toNumber(breakpoint.slidesPerView) : null;
        if (value !== null) values.push(value);
      });
    }

    if (usesAutoWidth(params)) {
      var containerWidth = element.clientWidth;
      var slideWidth = 0;

      slides.forEach(function (slide) {
        var width = slide.getBoundingClientRect().width;
        if (width > 0 && (slideWidth === 0 || width < slideWidth)) slideWidth = width;
      });

      values.push(containerWidth > 0 && slideWidth > 0 ? containerWidth / slideWidth : FALLBACK_SLIDES_PER_VIEW);
    }

    if (!values.length) values.push(toNumber(params.slidesPerView) || 1);

    return Math.max.apply(null, values);
  }

  function realSlidesOf(wrapper) {
    return Array.prototype.filter.call(wrapper.children, function (child) {
      return (
        child.classList &&
        child.classList.contains('swiper-slide') &&
        !child.hasAttribute('data-swiper-loop-fill') &&
        !child.classList.contains('swiper-slide-duplicate')
      );
    });
  }

  function cloneSlide(slide) {
    var clone = slide.cloneNode(true);

    clone.setAttribute('data-swiper-loop-fill', 'true');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');

    /* Never duplicate ids or theme-editor hooks — the editor would bind to the wrong node. */
    clone.querySelectorAll('[id]').forEach(function (node) {
      node.removeAttribute('id');
    });
    ['data-shopify-editor-block', 'data-shopify-editor-section'].forEach(function (attribute) {
      clone.removeAttribute(attribute);
      clone.querySelectorAll('[' + attribute + ']').forEach(function (node) {
        node.removeAttribute(attribute);
      });
    });

    return clone;
  }

  /* Re-measure once the images inside have a size — a slide measured before that is what renders blank. */
  function updateWhenImagesLoad(instance, element) {
    var images = element.querySelectorAll('img');
    var pending = 0;

    function done() {
      pending -= 1;
      if (pending <= 0 && instance && !instance.destroyed) instance.update();
    }

    images.forEach(function (image) {
      if (image.complete && image.naturalWidth > 0) return;
      pending += 1;
      image.addEventListener('load', done, { once: true });
      image.addEventListener('error', done, { once: true });
    });

    if (window.ResizeObserver) {
      var observer = new ResizeObserver(function () {
        if (!instance || instance.destroyed) {
          observer.disconnect();
          return;
        }
        instance.update();
      });

      observer.observe(element);
    }
  }

  /**
   * With filler clones in the DOM the pagination would render a bullet per clone too. Hide the surplus and drive the
   * active state from the real index, so the dots still read "1 of 4".
   */
  function keepPaginationReal(instance, realCount) {
    /* Sections can rename the bullet classes (bulletClass / bulletActiveClass), so read them off the instance. */
    function classTokens(value, fallback) {
      return String(value || fallback)
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    }

    function sync() {
      if (!instance || instance.destroyed || !instance.pagination || !instance.pagination.el) return;

      var paginationParams = instance.params.pagination || {};
      var bulletTokens = classTokens(paginationParams.bulletClass, 'swiper-pagination-bullet');
      var activeTokens = classTokens(paginationParams.bulletActiveClass, 'swiper-pagination-bullet-active');

      if (!bulletTokens.length) return;

      var elements = Array.isArray(instance.pagination.el) ? instance.pagination.el : [instance.pagination.el];

      elements.forEach(function (paginationEl) {
        if (!paginationEl) return;

        var bullets = paginationEl.querySelectorAll('.' + bulletTokens.join('.'));
        var active = instance.realIndex % realCount;

        bullets.forEach(function (bullet, index) {
          bullet.style.display = index < realCount ? '' : 'none';
          activeTokens.forEach(function (token) {
            bullet.classList.toggle(token, index === active);
          });
        });
      });
    }

    instance.on('paginationRender', sync);
    instance.on('paginationUpdate', sync);
    instance.on('slideChange', sync);
    instance.on('transitionEnd', sync);
    sync();
  }

  function prepare(element, options) {
    var params = Object.assign({}, options || {});

    /* Swiper 9+ renamed this. Translate rather than drop it, so the intent of older sections is kept. */
    if (params.loopedSlides !== undefined) {
      if (params.loopAdditionalSlides === undefined) params.loopAdditionalSlides = params.loopedSlides;
      delete params.loopedSlides;
    }

    /* Late layout changes (images, fonts, editor edits) should re-measure the track instead of stranding a slide. */
    if (params.observer === undefined) params.observer = true;
    if (params.observeParents === undefined) params.observeParents = true;
    if (params.observeSlideChildren === undefined) params.observeSlideChildren = true;
    if (params.watchOverflow === undefined) params.watchOverflow = true;

    var context = { params: params, realCount: 0, cloned: false };

    if (!params.loop || !element || !element.querySelector) return context;

    var wrapper = element.querySelector('.swiper-wrapper');
    if (!wrapper) return context;

    /* Drop any filler left over from a previous init (section reload in the theme editor). */
    Array.prototype.slice.call(wrapper.querySelectorAll('[data-swiper-loop-fill]')).forEach(function (node) {
      node.remove();
    });

    var slides = realSlidesOf(wrapper);
    context.realCount = slides.length;

    if (slides.length === 0) {
      params.loop = false;
      return context;
    }

    if (slides.length === 1) {
      params.loop = false;
      return context;
    }

    var needed = Math.ceil(maxSlidesPerView(element, params, slides)) * 2 + 1;
    if (slides.length >= needed) return context;

    /**
     * Clone in WHOLE sets only. A partial set would leave a total that is not a multiple of the real slide count, so
     * the loop would run A,B,C,D,A,B,C and then wrap — the last slide gets skipped on most passes. Whole sets keep
     * every slide appearing once per cycle and keep `realIndex % realCount` valid for the pagination.
     */
    var sets = Math.ceil(needed / slides.length);
    var maxSets = Math.floor(MAX_SLIDES / slides.length);

    if (maxSets < 2) return context; // no room for even one more whole set
    if (sets > maxSets) sets = maxSets;
    if (sets < 2) return context;

    var fragment = document.createDocumentFragment();
    var total = slides.length;

    for (var set = 1; set < sets; set += 1) {
      for (var index = 0; index < slides.length; index += 1) {
        fragment.appendChild(cloneSlide(slides[index]));
        total += 1;
      }
    }

    wrapper.appendChild(fragment);
    context.cloned = true;

    element.dispatchEvent(
      new CustomEvent('swiper-loop-fix:slides-cloned', {
        bubbles: true,
        detail: { realCount: slides.length, totalCount: total }
      })
    );

    return context;
  }

  function patch() {
    var Original = window.Swiper;

    if (typeof Original !== 'function' || Original.__loopFixApplied) return typeof Original === 'function';

    function resolveElement(target) {
      if (typeof target === 'string') return document.querySelector(target);
      if (target && target.nodeType === 1) return target;
      return null;
    }

    var Patched = function (target, options) {
      var element = resolveElement(target);
      var context = element ? prepare(element, options) : { params: options, realCount: 0, cloned: false };
      var instance = new Original(target, context.params);

      if (element) {
        updateWhenImagesLoad(instance, element);
        if (context.cloned && context.realCount > 1) keepPaginationReal(instance, context.realCount);
      }

      return instance;
    };

    /* Keep Swiper.use(), Swiper.extendDefaults() and the module statics reachable. */
    Patched.prototype = Original.prototype;
    Object.setPrototypeOf(Patched, Original);
    Patched.__loopFixApplied = true;

    window.Swiper = Patched;

    return true;
  }

  if (!patch()) {
    /* The bundle is loaded from a CDN — retry briefly in case it lands after this file. */
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (patch() || attempts > 100) clearInterval(timer);
    }, 50);
  }
})();
